import {
  Announcement03,
  BarChart07,
  BarChart10,
  BookOpen01,
  CursorBox,
  File02,
  File05,
  Home01,
  LineChartUp01,
  Link01,
  Lock01,
  MessageTextSquare01,
  Package,
  SearchMd,
  Send01,
  Settings04,
  Shield02,
  ShieldTick,
  ShieldZap,
  ShoppingBag01,
  Tag01,
  Target03,
  User01,
  UserPlus01,
  Users01,
  UserX01,
  VideoRecorder,
  ZapFast,
} from "@untitledui/icons";
import { useMerchantDetails } from "@/api/merchant-details";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useIsAssurance } from "@/hooks/use-is-assurance";
import { useIsHideExplorers } from "@/hooks/use-is-hide-explorers";
import { useIsInsights } from "@/hooks/use-is-insights";
import { useRole } from "@/hooks/use-role";
import { useAuth } from "@/providers/auth-provider";
import { useMerchant, useWorkspaceId } from "@/providers/MerchantProvider";
import type { NavItem } from "./sidebar-layout";

export function useMerchantNavigationSections(): NavItem[] {
  const { role } = useRole();
  const isRestrictedUser = role === "restricted_user";
  const isInsights = useIsInsights();
  const isAssurance = useIsAssurance();
  const isHideExplorers = useIsHideExplorers();
  const merchant = useMerchant();
  const workspaceId = useWorkspaceId();
  const { data: merchantDetails } = useMerchantDetails(merchant);
  const isDeRegion = merchantDetails?.ttsRegion === "DE";
  const creatorTagsEnabled = useFeatureFlag("F-creator-tags");
  const rulesDashboardEnabled = useFeatureFlag("F-rules-dashboard");
  const newCollabsGroupsEnabled = useFeatureFlag("F-new-collabs-groups");
  const mfaEnabled = useFeatureFlag("F-mfa");
  const { user } = useAuth();
  const isImpersonating = !!user?.impersonatedBy;

  const base = workspaceId ? "/workspace/$workspaceId/merchant/$merchantId" : "/merchant/$merchantId";

  const navigationSections: NavItem[] = [
    {
      enabled: true,
      id: "home",
      label: "Home",
      icon: Home01,
      path: `${base}/home`,
    },
    {
      enabled: true,
      id: "safety",
      // TODO: confirm final parent icon for Safety
      icon: ShieldTick,
      label: "Safety",
      description: "Protect your brand, review flagged videos and set your policies",
      subItems: [
        {
          id: "safety-overview",
          label: "Safety Overview",
          icon: ShieldZap,
          path: `${base}/safety/overview`,
          enabled: isRestrictedUser || isAssurance,
        },
        {
          id: "flagged-videos",
          label: "Flagged Videos",
          icon: Shield02,
          path: `${base}/safety/flagged`,
          enabled: isRestrictedUser || isAssurance,
        },
        {
          id: "configure-safety",
          label: "Configure Safety",
          icon: CursorBox,
          path: `${base}/safety/configure`,
          enabled: isRestrictedUser || isAssurance,
        },
      ],
    },
    {
      enabled: !isHideExplorers && !isRestrictedUser,
      id: "creators",
      label: "Creators",
      icon: Users01,
      description: "Curate your roster, find new creators and talk to them",
      subItems: [
        {
          id: "creator-explorer",
          label: "Your Creators",
          icon: Users01,
          path: `${base}/creators`,
        },
        {
          id: "creator-cohorts",
          label: "Cohorts",
          icon: Tag01,
          path: "/merchant/$merchantId/creators/cohorts",
        },
        {
          id: "creator-tags",
          label: "Tags",
          icon: Tag01,
          path: `${base}/tags`,
          enabled: creatorTagsEnabled,
        },
        {
          id: "blocked-creators",
          label: "Blocked Creators",
          icon: UserX01,
          path: `${base}/creators/blocked`,
        },
        {
          id: "rules",
          label: "Rules",
          icon: ZapFast,
          path: `${base}/creators/rules`,
          enabled: rulesDashboardEnabled,
        },
        {
          label: "FIND",
          items: [
            {
              id: "candidate-explorer",
              label: "New Creators",
              icon: SearchMd,
              path: `${base}/creators/find`,
              enabled: !isDeRegion,
            },
          ],
        },
        {
          label: "MESSAGE",
          items: [
            {
              id: "activities",
              label: "Messages & Activity",
              icon: MessageTextSquare01,
              path: `${base}/creators/messages`,
              enabled: true,
            },
            {
              id: "sequences",
              label: "Automate Outreach",
              icon: Send01,
              path: "/merchant/$merchantId/automate-outreach",
            },
            {
              id: "message-templates",
              label: "Message Templates",
              icon: File02,
              path: "/merchant/$merchantId/creators/message-templates",
            },
          ],
        },
      ],
    },
    {
      enabled: !isRestrictedUser,
      id: "collabs",
      label: "Collabs",
      icon: Announcement03,
      description: "Manage your collaborations and sample requests",
      subItems: [
        {
          id: newCollabsGroupsEnabled ? "collab-groups-target" : "collabs-target",
          label: "Target",
          icon: Target03,
          path: newCollabsGroupsEnabled ? `${base}/collabs/groups/target` : `${base}/collabs/target`,
        },
        {
          id: newCollabsGroupsEnabled ? "collab-groups-open" : "collabs-open",
          label: "Open",
          icon: BookOpen01,
          path: newCollabsGroupsEnabled ? `${base}/collabs/groups/open` : `${base}/collabs/open`,
        },
        {
          id: "sample-requests",
          label: "Sample Requests",
          icon: Package,
          path: "/merchant/$merchantId/collabs/samples",
        },
      ],
    },
    {
      enabled: !isRestrictedUser,
      id: "analytics",
      label: "Analytics",
      icon: BarChart10,
      description: "Measure performance and identify what's driving GMV",
      subItems: [
        {
          id: "analytics-dashboard",
          label: "Performance Overview",
          icon: BarChart07,
          path: `${base}/analytics/overview`,
          enabled: !isRestrictedUser && isInsights,
        },
        {
          id: "reports",
          label: "Reports",
          icon: LineChartUp01,
          path: `${base}/analytics/reports`,
          enabled: !isRestrictedUser && isInsights,
        },
      ],
    },
    {
      enabled: !isHideExplorers && !isRestrictedUser,
      id: "video-explorer",
      label: "Videos",
      icon: VideoRecorder,
      path: `${base}/videos`,
    },
    {
      enabled: !isHideExplorers && !isRestrictedUser,
      id: "products",
      label: "Products",
      icon: ShoppingBag01,
      description: "Track product performance and brief creators to drive GMV",
      subItems: [
        {
          id: "product-explorer",
          label: "All Products",
          icon: ShoppingBag01,
          path: `${base}/products/all`,
        },
        {
          id: "smart-briefs",
          label: "Smart Product Briefs",
          icon: File05,
          path: `${base}/products/briefs`,
        },
      ],
    },
    {
      enabled: true,
      id: "settings",
      label: "Settings",
      icon: Settings04,
      description: "Manage your account and preferences",
      subItems: [
        {
          label: "MERCHANT SETTINGS",
          items: [
            {
              id: "merchant-details",
              label: "Merchant Details",
              icon: File02,
              path: `${base}/settings/merchant-details`,
            },
            {
              id: "team",
              label: "Team",
              icon: UserPlus01,
              path: `${base}/settings/team`,
            },
            {
              id: "integrations",
              label: "Integrations",
              icon: Link01,
              path: `${base}/settings/integrations`,
              enabled: true,
            },
          ],
        },
        {
          label: "PERSONAL SETTINGS",
          items: [
            {
              id: "my-details",
              label: "My Details",
              icon: User01,
              path: `${base}/settings/my-details`,
              enabled: !isImpersonating,
            },
            {
              id: "security",
              label: "Security",
              icon: Lock01,
              path: `${base}/settings/security`,
              enabled: mfaEnabled && !isImpersonating,
            },
          ],
        },
      ],
    },
  ];

  return navigationSections;
}
